import React, { useState } from 'react';
import { useFacturationModeConfig, useToggleFacturationMode, useToggleAllowManualForce } from '../hooks/useFacturationMode';
import { usePredictiveModConfig } from '../hooks/usePredictiveModControl';

interface FacturationModeToggleProps {
  showLabel?: boolean;
}

export const FacturationModeToggle: React.FC<FacturationModeToggleProps> = ({ showLabel = true }) => {
  // Polling cada 3 segundos para mantener el toggle actualizado en tiempo real
  const { config, loading, refetch } = useFacturationModeConfig(3000);
  const { toggle, loading: toggling } = useToggleFacturationMode();
  const { toggleForce, loading: togglingForce } = useToggleAllowManualForce();
  // Usar Control Predictivo para bloquear el toggle manual
  const { config: predictiveConfig } = usePredictiveModConfig();
  const autoControlEnabled = predictiveConfig?.enabled || false;

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingState, setPendingState] = useState<boolean>(false);
  const [reason, setReason] = useState('');
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'warning';
    message: string;
  } | null>(null);

  const currentUser = 'admin';
  const currentUserName = 'Administrador';

  const handleToggleClick = (newState: boolean) => {
    if (newState === config?.enabled) return;

    // Si el control predictivo está activo, no permitir cambios manuales
    if (autoControlEnabled) {
      setNotification({
        type: 'warning',
        message: 'Control Predictivo activo. Desactívelo en "Control Predictivo" para cambiar manualmente.',
      });
      setTimeout(() => setNotification(null), 5000);
      return;
    }

    setPendingState(newState);
    setReason('');
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    try {
      const result = await toggle({
        enabled: pendingState,
        changedBy: currentUser,
        changedByName: currentUserName,
        reason: reason || undefined,
      });

      if (result?.syncResult?.success) {
        setNotification({
          type: 'success',
          message: `Modo especial ${pendingState ? 'ACTIVADO' : 'DESACTIVADO'}`,
        });
      } else {
        setNotification({
          type: 'warning',
          message: `Modo ${pendingState ? 'activado' : 'desactivado'} localmente. Sync pendiente.`,
        });
      }

      refetch();
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: `Error: ${error.message}`,
      });
    } finally {
      setShowConfirmModal(false);
      setReason('');
    }

    setTimeout(() => setNotification(null), 4000);
  };

  if (loading && !config) {
    return (
      <div className="facturation-toggle-wrapper">
        <span className="toggle-loading-text">Cargando...</span>
      </div>
    );
  }

  return (
    <>
      <div className={`facturation-toggle-wrapper ${autoControlEnabled ? 'auto-controlled' : ''}`}>
        {showLabel && (
          <span className="toggle-mode-label">
            Modo Especial POS
            {autoControlEnabled && (
              <span className="auto-indicator" title="Control Predictivo Activo">AUTO</span>
            )}
          </span>
        )}

        <div className={`header-toggle-container ${config?.enabled ? 'enabled' : 'disabled'}`}>
          <label className="header-toggle-switch">
            <input
              type="checkbox"
              checked={config?.enabled || false}
              onChange={(e) => handleToggleClick(e.target.checked)}
              disabled={toggling || loading || autoControlEnabled}
            />
            <span className="header-toggle-slider">
              <span className="toggle-text-on">ON</span>
              <span className="toggle-text-off">OFF</span>
            </span>
          </label>
        </div>

        {toggling && <span className="toggle-processing">...</span>}

        {/* Mini switch: Permitir Forzado Manual (independiente del estado MOD) */}
        <div
          className="force-toggle-mini"
          title={config?.allowManualForce ? 'Forzado manual PERMITIDO' : 'Forzado manual BLOQUEADO'}
        >
          <label className="mini-switch">
            <input
              type="checkbox"
              checked={config?.allowManualForce ?? true}
              onChange={(e) => toggleForce(e.target.checked)}
              disabled={togglingForce}
            />
            <span className="mini-slider" />
          </label>
          <span className="force-label">✓✓</span>
        </div>
      </div>

      {/* Notificación flotante */}
      {notification && (
        <div className={`header-notification ${notification.type}`}>
          {notification.message}
          <button onClick={() => setNotification(null)}>×</button>
        </div>
      )}

      {/* Modal de confirmación */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              {pendingState ? 'Activar' : 'Desactivar'} Modo Especial POS
            </h3>

            <div className={`confirm-warning ${pendingState ? 'enable' : 'disable'}`}>
              {pendingState ? (
                <p>
                  <strong>Al activar:</strong> Las facturas que cumplan los criterios
                  (efectivo 100%, consumidor final) NO se enviarán a la DIAN.
                </p>
              ) : (
                <p>
                  <strong>Al desactivar:</strong> Todas las facturas se procesarán
                  como facturación electrónica normal.
                </p>
              )}
            </div>

            <div className="confirm-form-group">
              <label>Razón (opcional)</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Motivo del cambio..."
              />
            </div>

            <div className="confirm-actions">
              <button
                className={`confirm-btn ${pendingState ? 'btn-enable' : 'btn-disable'}`}
                onClick={handleConfirm}
                disabled={toggling}
              >
                {toggling ? 'Procesando...' : (pendingState ? 'Activar' : 'Desactivar')}
              </button>
              <button
                className="confirm-btn btn-cancel"
                onClick={() => setShowConfirmModal(false)}
                disabled={toggling}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .facturation-toggle-wrapper {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 1rem;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          backdrop-filter: blur(10px);
        }

        .facturation-toggle-wrapper.auto-controlled {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%);
          border: 1px solid rgba(59, 130, 246, 0.3);
          animation: autoGlow 2s ease-in-out infinite;
        }

        @keyframes autoGlow {
          0%, 100% {
            box-shadow: 0 0 5px rgba(59, 130, 246, 0.3);
          }
          50% {
            box-shadow: 0 0 15px rgba(59, 130, 246, 0.5);
          }
        }

        .auto-indicator {
          display: inline-block;
          font-size: 0.6rem;
          font-weight: 700;
          background: linear-gradient(135deg, #3b82f6 0%, #10b981 100%);
          color: white;
          padding: 0.15rem 0.4rem;
          border-radius: 4px;
          margin-left: 0.5rem;
          vertical-align: middle;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        .toggle-mode-label {
          font-size: 0.85rem;
          font-weight: 600;
          color: white;
          white-space: nowrap;
        }

        .toggle-loading-text {
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.7);
        }

        .toggle-processing {
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.7);
          animation: blink 1s infinite;
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        /* Mini switch forzado manual */
        .force-toggle-mini {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-left: 8px;
          padding-left: 8px;
          border-left: 1px solid rgba(255, 255, 255, 0.2);
        }

        .force-label {
          font-size: 0.7rem;
          color: rgba(255, 255, 255, 0.6);
          letter-spacing: -1px;
          font-weight: 600;
        }

        .mini-switch {
          position: relative;
          display: inline-block;
          width: 28px;
          height: 16px;
        }

        .mini-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .mini-slider {
          position: absolute;
          cursor: pointer;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          transition: 0.2s;
        }

        .mini-slider::before {
          content: '';
          position: absolute;
          height: 12px;
          width: 12px;
          left: 2px;
          bottom: 2px;
          background: white;
          border-radius: 50%;
          transition: 0.2s;
        }

        .mini-switch input:checked + .mini-slider {
          background: #60a5fa;
        }

        .mini-switch input:checked + .mini-slider::before {
          transform: translateX(12px);
        }

        .header-toggle-container {
          position: relative;
        }

        .header-toggle-switch {
          position: relative;
          display: inline-block;
          width: 70px;
          height: 32px;
          cursor: pointer;
        }

        .header-toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .header-toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #dc3545;
          transition: 0.3s;
          border-radius: 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 8px;
          box-shadow: inset 0 2px 4px rgba(0,0,0,0.2);
        }

        .header-toggle-slider:before {
          position: absolute;
          content: "";
          height: 26px;
          width: 26px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          z-index: 2;
        }

        .toggle-text-on,
        .toggle-text-off {
          font-size: 0.65rem;
          font-weight: 700;
          color: white;
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
          z-index: 1;
        }

        .toggle-text-on {
          opacity: 0;
        }

        .toggle-text-off {
          opacity: 1;
        }

        .header-toggle-switch input:checked + .header-toggle-slider {
          background-color: #28a745;
        }

        .header-toggle-switch input:checked + .header-toggle-slider:before {
          transform: translateX(38px);
        }

        .header-toggle-switch input:checked + .header-toggle-slider .toggle-text-on {
          opacity: 1;
        }

        .header-toggle-switch input:checked + .header-toggle-slider .toggle-text-off {
          opacity: 0;
        }

        .header-toggle-switch input:disabled + .header-toggle-slider {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Notificación flotante */
        .header-notification {
          position: fixed;
          top: 80px;
          right: 20px;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 1rem;
          z-index: 10000;
          animation: slideIn 0.3s ease;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .header-notification.success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .header-notification.error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .header-notification.warning {
          background: #fff3cd;
          color: #856404;
          border: 1px solid #ffeeba;
        }

        .header-notification button {
          background: none;
          border: none;
          font-size: 1.2rem;
          cursor: pointer;
          opacity: 0.7;
          padding: 0;
          line-height: 1;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        /* Modal de confirmación */
        .confirm-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10001;
        }

        .confirm-modal {
          background: var(--card-bg, white);
          border-radius: 12px;
          padding: 1.5rem;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        }

        .confirm-modal h3 {
          margin: 0 0 1rem;
          font-size: 1.2rem;
          color: var(--text-primary, #333);
        }

        .confirm-warning {
          padding: 0.75rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }

        .confirm-warning.enable {
          background: #fff3cd;
          border: 1px solid #ffc107;
          color: #856404;
        }

        .confirm-warning.disable {
          background: #e2e3e5;
          border: 1px solid #d6d8db;
          color: #383d41;
        }

        .confirm-warning p {
          margin: 0;
        }

        .confirm-form-group {
          margin-bottom: 1rem;
        }

        .confirm-form-group label {
          display: block;
          font-size: 0.85rem;
          font-weight: 500;
          margin-bottom: 0.25rem;
          color: var(--text-secondary, #666);
        }

        .confirm-form-group input {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid var(--border-color, #ddd);
          border-radius: 6px;
          font-size: 0.9rem;
        }

        .confirm-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
        }

        .confirm-btn {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .confirm-btn.btn-enable {
          background: #28a745;
          color: white;
        }

        .confirm-btn.btn-enable:hover {
          background: #218838;
        }

        .confirm-btn.btn-disable {
          background: #dc3545;
          color: white;
        }

        .confirm-btn.btn-disable:hover {
          background: #c82333;
        }

        .confirm-btn.btn-cancel {
          background: #6c757d;
          color: white;
        }

        .confirm-btn.btn-cancel:hover {
          background: #5a6268;
        }

        .confirm-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
};
